import { baseApi } from "@/shared/api";
import type {
  Task,
  CreateTaskDto,
  UpdateTaskDto,
  ReorderTasksDto,
  TaskComment,
  AddCommentDto,
  EditCommentDto,
  ToggleFavoriteResponse,
  CrossProjectBoardResponse,
  GoalProject,
} from "../types";

export const tasksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTask: builder.query<Task, { projectId: number; taskId: number }>({
      query: ({ projectId, taskId }) => ({
        url: `/api/projects/${projectId}/tasks/${taskId}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, { taskId }) => [{ type: "Tasks", id: taskId }],
    }),
    getCrossProjectBoard: builder.query<CrossProjectBoardResponse, void>({
      query: () => ({
        url: "/api/tasks/board/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "TaskBoard", id: "LIST" }],
    }),
    getGoals: builder.query<GoalProject[], void>({
      query: () => ({
        url: "/api/tasks/goals/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "TaskGoals", id: "LIST" }],
    }),
    createTask: builder.mutation<Task, { projectId: number; data: CreateTaskDto }>({
      query: ({ projectId, data }) => ({
        url: `/api/projects/${projectId}/tasks/`,
        method: "POST",
        body: data,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "Tasks", id: "LIST" },
        { type: "Projects", id: `BOARD-${projectId}` },
        { type: "Projects", id: projectId },
        { type: "AuditEntries", id: "PROJECT_LIST" },
        { type: "TaskBoard", id: "LIST" },
        { type: "TaskGoals", id: "LIST" },
      ],
    }),
    updateTask: builder.mutation<Task, { projectId: number; id: number; data: UpdateTaskDto }>({
      query: ({ projectId, id, data }) => ({
        url: `/api/projects/${projectId}/tasks/${id}/`,
        method: "PATCH",
        body: data,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId, id }) => [
        { type: "Tasks", id: "LIST" },
        { type: "Tasks", id },
        { type: "Projects", id: `BOARD-${projectId}` },
        { type: "Projects", id: projectId },
        { type: "AuditEntries", id: "PROJECT_LIST" },
        { type: "AuditEntries", id: `task-${id}` },
        { type: "TaskBoard", id: "LIST" },
        { type: "TaskGoals", id: "LIST" },
      ],
    }),
    deleteTask: builder.mutation<void, { projectId: number; id: number }>({
      query: ({ projectId, id }) => ({
        url: `/api/projects/${projectId}/tasks/${id}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "Tasks", id: "LIST" },
        { type: "Projects", id: `BOARD-${projectId}` },
        { type: "Projects", id: projectId },
        { type: "AuditEntries", id: "PROJECT_LIST" },
        { type: "TaskBoard", id: "LIST" },
        { type: "TaskGoals", id: "LIST" },
      ],
    }),
    reorderTasks: builder.mutation<void, { projectId: number; data: ReorderTasksDto }>({
      query: ({ projectId, data }) => ({
        url: `/api/projects/${projectId}/tasks/reorder/`,
        method: "POST",
        body: data,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, error, { projectId }) =>
        error
          ? [{ type: "Projects", id: `BOARD-${projectId}` }]
          : [
              { type: "TaskBoard", id: "LIST" },
              { type: "Projects", id: `BOARD-${projectId}` },
              { type: "Projects", id: projectId },
            ],
    }),
    addComment: builder.mutation<TaskComment, AddCommentDto>({
      query: ({ projectId, taskId, body, files }) => {
        if (files && files.length > 0) {
          const formData = new FormData();
          formData.append("body", body);
          files.forEach((file) => formData.append("files", file));
          return {
            url: `/api/projects/${projectId}/tasks/${taskId}/comments/`,
            method: "POST",
            body: formData,
            credentials: "include" as const,
          };
        }
        return {
          url: `/api/projects/${projectId}/tasks/${taskId}/comments/`,
          method: "POST",
          body: { body },
          credentials: "include" as const,
        };
      },
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: "Tasks", id: taskId },
        { type: "TaskComments", id: "LIST" },
        { type: "AuditEntries", id: `task-${taskId}` },
        { type: "AuditEntries", id: "PROJECT_LIST" },
      ],
    }),
    editComment: builder.mutation<TaskComment, EditCommentDto>({
      query: ({ projectId, taskId, commentId, body, files, removeAttachmentIds }) => {
        const hasFiles = files && files.length > 0;
        const hasRemovals = removeAttachmentIds && removeAttachmentIds.length > 0;
        if (hasFiles || hasRemovals) {
          const formData = new FormData();
          formData.append("body", body);
          if (hasFiles) files.forEach((file) => formData.append("files", file));
          if (hasRemovals) removeAttachmentIds.forEach((id) => formData.append("remove_attachment_ids", String(id)));
          return {
            url: `/api/projects/${projectId}/tasks/${taskId}/comments/${commentId}/`,
            method: "PATCH",
            body: formData,
            credentials: "include" as const,
          };
        }
        return {
          url: `/api/projects/${projectId}/tasks/${taskId}/comments/${commentId}/`,
          method: "PATCH",
          body: { body },
          credentials: "include" as const,
        };
      },
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: "Tasks", id: taskId },
        { type: "TaskComments", id: "LIST" },
        { type: "AuditEntries", id: `task-${taskId}` },
        { type: "AuditEntries", id: "PROJECT_LIST" },
      ],
    }),
    deleteComment: builder.mutation<void, { projectId: number; taskId: number; commentId: number }>({
      query: ({ projectId, taskId, commentId }) => ({
        url: `/api/projects/${projectId}/tasks/${taskId}/comments/${commentId}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: "Tasks", id: taskId },
        { type: "TaskComments", id: "LIST" },
        { type: "AuditEntries", id: `task-${taskId}` },
        { type: "AuditEntries", id: "PROJECT_LIST" },
      ],
    }),
    toggleFavorite: builder.mutation<ToggleFavoriteResponse, { projectId: number; taskId: number }>({
      query: ({ projectId, taskId }) => ({
        url: `/api/projects/${projectId}/tasks/${taskId}/toggle_favorite/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId, taskId }) => [
        { type: "Tasks", id: "LIST" },
        { type: "Tasks", id: taskId },
        { type: "Projects", id: `BOARD-${projectId}` },
        { type: "TaskBoard", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetTaskQuery,
  useGetCrossProjectBoardQuery,
  useGetGoalsQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useReorderTasksMutation,
  useAddCommentMutation,
  useEditCommentMutation,
  useDeleteCommentMutation,
  useToggleFavoriteMutation,
} = tasksApi;
